import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { saveTransactions } from '../store/transactionsSlice';
import { parseCSV } from '../utils/csvParser';
import { uploadFile, deleteFile } from '../utils/storage';
import { Transaction } from '../types/transaction';
import { useUser } from '@clerk/clerk-react';
import Button from '../components/ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { CleanupButton } from '../components/CleanupButton';
import { AppDispatch } from '../store';
import FileUploader from '../components/FileUploader';
import { usePlaidLink } from '../hooks/usePlaidLink';

const UploadPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useUser();
  const [error, setError] = useState<string | null>(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const { open, ready } = usePlaidLink();

  useEffect(() => {
    // Check if file was just deleted
    const wasFileDeleted = sessionStorage.getItem('fileDeleted');
    if (wasFileDeleted) {
      setShowDeleteSuccess(true);
      sessionStorage.removeItem('fileDeleted');
      
      // Hide the success message after 3 seconds
      const timer = setTimeout(() => {
        setShowDeleteSuccess(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleFileUpload = async (file: File) => {
    setError(null);
    
    if (!user) {
      setError('You must be logged in to upload files');
      return;
    }

    try {
      console.log('Starting file processing...', file.name);
      console.log('File size:', file.size, 'bytes');
      
      // Upload file to Firebase Storage
      const fileUrl = await uploadFile(file, user.id);
      console.log('File uploaded to:', fileUrl);
      
      // Process file and get transactions
      const transactions = await parseCSV(file);
      console.log('Parsed transactions:', transactions.length);
      
      if (transactions.length === 0) {
        setError('No valid transactions found in the file');
        // Clean up the uploaded file if no valid transactions
        await deleteFile(`users/${user.id}/${file.name}`);
        return;
      }
      
      // Add user ID and file reference to each transaction
      const transactionsWithUser = transactions.map(transaction => ({
        ...transaction,
        userId: user.id,
        timestamp: new Date().toISOString(),
        sourceFile: fileUrl
      }));
      
      // Save transactions to Firestore
      const result = await dispatch(saveTransactions(transactionsWithUser)).unwrap();
      console.log('Saved transactions result:', {
        saved: result.length,
        expected: transactionsWithUser.length
      });
      
      // Set data source before navigating
      sessionStorage.setItem('dataSource', 'fileUpload');
      
      // Navigate to loading page
      navigate('/loading');
    } catch (error) {
      console.error('Error processing file:', error);
      // Clean up the uploaded file if there was an error
      if (file) {
        await deleteFile(`users/${user.id}/${file.name}`).catch(console.error);
      }
      setError(error instanceof Error ? error.message : 'An error occurred while processing the file');
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5FF]">
      <div className="h-screen flex flex-col justify-between px-4 sm:px-6 py-12 sm:py-16">
        <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mt-1"
          >
            <h1 className="text-[32px] sm:text-[38px] leading-[120%] font-black text-[#221A51]">
              Find out what's
              <br />
              in your report
            </h1>
            <p className="text-[14px] sm:text-[16px] leading-[150%] mt-2 text-[#221A51]/60">
              Connect your bank accounts directly or upload a CSV file to see your 
              financial year in review.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center my-10 sm:my-14"
          >
            <img 
              src="/freepik_br_f24a9265-8760-470e-b4a5-ea6788edf490 1.png" 
              alt="Upload illustration" 
              className="w-40 sm:w-48 h-auto"
            />
          </motion.div>

          <div className="space-y-4">
            {/* Connect Accounts Button Row */}
            <div className="w-full">
              <button
                onClick={open}
                disabled={!ready}
                className="w-full py-3 bg-[#FF8C77] text-white text-center
                       text-lg font-bold rounded-full cursor-pointer
                       border-2 border-[#B85F4D] shadow-[4px_4px_0px_0px_#B85F4D]
                       hover:bg-[#FF8C77]/90 active:translate-x-[4px] active:translate-y-[4px] 
                       active:shadow-none transition-all duration-200
                       disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Connect Accounts
              </button>
            </div>

            {/* Choose File and Clear Transactions Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="w-full">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls,.pdf"
                  onChange={(e) => e.target.files && handleFileUpload(e.target.files[0])}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="block w-full py-3 bg-[#FF8C77] text-white text-center
                           text-lg font-bold rounded-full cursor-pointer
                           border-2 border-[#B85F4D] shadow-[4px_4px_0px_0px_#B85F4D]
                           hover:bg-[#FF8C77]/90 active:translate-x-[4px] active:translate-y-[4px] 
                           active:shadow-none transition-all duration-200"
                >
                  Choose a file
                </label>
              </div>

              <div>
                <CleanupButton />
              </div>
            </div>

            {error && (
              <div className="mt-2">
                <p className="text-sm text-center text-red-500">{error}</p>
              </div>
            )}
          </div>

          <div className="mt-8 text-center">
            <p className="text-[#221A51]/60 text-sm">
              Connect your bank accounts directly or
            </p>
            <a href="#" className="text-[#221A51] underline text-sm">
              upload a CSV file
            </a>
          </div>
        </div>

        {/* Delete Success Message */}
        <AnimatePresence>
          {showDeleteSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed top-4 sm:top-6 left-1/2 -translate-x-1/2 
                       bg-white px-4 py-3 sm:px-6 sm:py-4 rounded-xl
                       shadow-brutal border-2 border-black z-50
                       max-w-[280px] sm:max-w-[320px] mx-auto"
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <p className="text-sm sm:text-base font-medium">
                  Successfully cleared all transactions
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default UploadPage;